// client/src/components/plataforma/siteInstitucional/BannersHomeFormPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBanners } from '../../../hooks/useBanners';
import useAlert from '../../../hooks/useAlert';
import { Save, Image, Loader2, XCircle, Megaphone, Plus, Trash2, Sparkles } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FabSaveButton from '../../common/FabSaveButton';
import { resizeImageToCover } from '../../../utils/imageResize';

// Converte o base64 devolvido pela geração por IA num File de verdade —
// dali pra frente é tratado exatamente como um arquivo escolhido manualmente
// (mesmo estado desktopFile/preview, mesmo upload no submit).
const base64ToFile = (base64, mimeType, filename) => {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    const byteArray = new Uint8Array(byteNumbers);
    return new File([byteArray], filename, { type: mimeType });
};

// Geração de imagem por IA (Gemini) — pede um prompt, gera na proporção do
// tipo de banner selecionado, e deixa o admin escolher em qual slot
// (desktop/tablet/mobile) usar o resultado. Não salva nada sozinho; quem usa
// esse componente decide o que fazer com o arquivo gerado.
const GerarComIA = ({ tipo, typeInfo, onUseImage, generateBannerImage }) => {
    const [prompt, setPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [resultado, setResultado] = useState(null); // { file, previewUrl, avisos }
    const [erro, setErro] = useState(null);

    const handleGerar = async () => {
        if (!prompt.trim() || !tipo) return;
        setIsGenerating(true);
        setErro(null);
        try {
            const data = await generateBannerImage(prompt, tipo);
            const ext = data.mimeType === 'image/png' ? 'png' : 'jpg';
            const file = base64ToFile(data.image, data.mimeType, `ia-${Date.now()}.${ext}`);
            const previewUrl = URL.createObjectURL(file);
            setResultado({ file, previewUrl, avisos: data.avisos });
        } catch (err) {
            setErro(err.message);
        } finally {
            setIsGenerating(false);
        }
    };

    useEffect(() => {
        return () => {
            if (resultado?.previewUrl) URL.revokeObjectURL(resultado.previewUrl);
        };
    }, [resultado]);

    return (
        <div className="form-section mt-6 border-t pt-6">
            <h3 className="form-section-title mb-2 flex items-center gap-2">
                <Sparkles size={18} className="text-purple-600" /> Gerar Imagem com IA
            </h3>
            <p className="form-hint mb-3">
                {typeInfo
                    ? <>A imagem sai pronta na proporção <strong>{typeInfo.aspectRatio}</strong> ({typeInfo.width}×{typeInfo.height}px) do tipo selecionado.</>
                    : 'Selecione um Tipo de Banner acima pra habilitar a geração.'}
            </p>
            <div className="flex flex-col md:flex-row gap-3">
                <textarea
                    className="form-input flex-grow"
                    rows={2}
                    placeholder='Ex: "banner promocional de salão de beleza, cores rosa e dourado, sem texto"'
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    disabled={!tipo || isGenerating}
                />
                <button
                    type="button"
                    className="action-button action-button-secondary"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={handleGerar}
                    disabled={!tipo || !prompt.trim() || isGenerating}
                >
                    {isGenerating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                    {isGenerating ? 'Gerando...' : 'Gerar'}
                </button>
            </div>

            {erro && <p className="text-red-600 text-sm mt-2">{erro}</p>}

            {resultado && (
                <div className="mt-4 p-3 border rounded bg-gray-50 flex flex-col md:flex-row items-start gap-4">
                    <img src={resultado.previewUrl} alt="Gerado por IA" className="rounded" style={{ maxWidth: 240, maxHeight: 160, objectFit: 'contain' }} />
                    <div className="flex-grow">
                        {resultado.avisos && (
                            <p className="text-amber-600 text-xs mb-2">
                                ⚠️ {resultado.avisos.join('; ')}
                            </p>
                        )}
                        <div className="flex flex-wrap gap-2">
                            <button type="button" className="action-button action-button-primary" onClick={() => onUseImage('desktop', resultado.file)}>Usar como Desktop</button>
                            <button type="button" className="action-button action-button-secondary" onClick={() => onUseImage('tablet', resultado.file)}>Usar como Tablet</button>
                            <button type="button" className="action-button action-button-secondary" onClick={() => onUseImage('mobile', resultado.file)}>Usar como Mobile</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// aspectRatio vem no formato "16:9"/"1:1"/"9:16"/"4:3" (bannerConfig.js) —
// CSS aspect-ratio usa "/" em vez de ":". A preview mostra a imagem já na
// proporção real do tipo de banner selecionado (largura variável, altura
// fixa), em vez de uma caixinha genérica igual pra qualquer tipo — assim dá
// pra perceber visualmente, antes de salvar, se a imagem escolhida não bate
// com um banner vertical/quadrado/largo.
const ImageUploader = ({ label, hint, file, setFile, preview, setPreview, aspectRatio, onRemove }) => {
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFile(file);
            const previewUrl = URL.createObjectURL(file);
            setPreview(previewUrl);
        }
    };
    useEffect(() => {
        return () => {
            if (preview && preview.startsWith('blob:')) {
                URL.revokeObjectURL(preview);
            }
        };
    }, [preview]);

    const cssAspectRatio = aspectRatio ? aspectRatio.replace(':', ' / ') : null;

    return (
        <div className="form-group">
            <label className="font-medium">{label}</label>
            <div className="flex items-start gap-4 mt-2">
                <input type="file" accept="image/*" onChange={handleImageChange} className="form-input flex-grow" />
                {preview && (
                    <div className="relative flex-shrink-0">
                        <img
                            src={preview}
                            alt="Preview"
                            className="rounded border border-gray-200 object-cover bg-gray-50"
                            style={{
                                height: 120,
                                width: cssAspectRatio ? 'auto' : 128,
                                aspectRatio: cssAspectRatio || undefined,
                                maxWidth: 220
                            }}
                        />
                        <button type="button" onClick={onRemove} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600" title="Remover imagem">
                            <XCircle size={18} />
                        </button>
                    </div>
                )}
            </div>
            {hint && <p className="form-hint">{hint}</p>}
        </div>
    );
};

const BannersHomeFormPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = !!id;
    const { createBanner, updateBanner, getBanner, uploadBannerMedia, generateBannerImage, options, loading: hookLoading } = useBanners();
    const { setAlert } = useAlert();
    
    const [formData, setFormData] = useState({
        title: '',
        subtitle: '',
        description: '',
        tipo: 'horizontal',
        status: 'active',
        link: { url: '', text: '', target: '_self' },
        buttons: [],
        items: [],
        media: { desktop: '', tablet: '', mobile: '' },
        altText: '',
        tags: [],
        pedido: 0
    });
    
    const [desktopFile, setDesktopFile] = useState(null);
    const [tabletFile, setTabletFile] = useState(null);
    const [mobileFile, setMobileFile] = useState(null);
    const [desktopPreview, setDesktopPreview] = useState(null);
    const [tabletPreview, setTabletPreview] = useState(null);
    const [mobilePreview, setMobilePreview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    // Guarda o _id do banner assim que ele é criado — se o upload de imagem
    // falhar depois (ex.: proporção incorreta) e o admin clicar em Salvar de
    // novo, o retry precisa cair em updateBanner(createdBannerId, ...), não
    // criar OUTRO banner do zero. Sem isso, cada nova tentativa duplicava o
    // banner (título/tipo repetidos, cada um sem imagem nenhuma).
    const [createdBannerId, setCreatedBannerId] = useState(null);

    const fetchBannerData = useCallback(async (bannerId) => {
        if (!bannerId) return;
        setIsLoading(true);
        try {
            const response = await getBanner(bannerId);
            const bannerData = response.data;
            setFormData({
                title: bannerData.title || '',
                subtitle: bannerData.subtitle || '',
                description: bannerData.description || '',
                tipo: bannerData.tipo || 'horizontal',
                status: bannerData.status || 'active',
                link: bannerData.link || { url: '', text: '', target: '_self' },
                buttons: bannerData.buttons || [],
                items: bannerData.items || [],
                media: bannerData.media || {},
                altText: bannerData.altText || '',
                tags: bannerData.tags || [],
                pedido: bannerData.pedido || 0
            });
            if (bannerData.imageUrls) {
                setDesktopPreview(bannerData.imageUrls.desktop);
                setTabletPreview(bannerData.imageUrls.tablet);
                setMobilePreview(bannerData.imageUrls.mobile);
            }
        } catch (error) {
            setAlert(error.message, 'error');
        } finally {
            setIsLoading(false);
        }
    }, [getBanner, setAlert]);

    useEffect(() => {
        if (isEditing) {
            fetchBannerData(id);
        }
    }, [id, isEditing, fetchBannerData]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Sem isso o form deixava salvar sem nenhuma imagem — o banner era
        // criado "com sucesso" (só os dados de texto) e ficava sem nenhuma
        // mídia, sem o admin perceber até checar a lista.
        if (!desktopFile && !desktopPreview) {
            setAlert('Selecione uma imagem para Desktop antes de salvar.', 'warning');
            return;
        }

        setIsLoading(true);
        try {
            // Se um envio anterior já criou o banner (texto salvo) e só o
            // upload da imagem falhou, esta tentativa precisa atualizar
            // esse MESMO banner — nunca criar um novo.
            const targetId = isEditing ? id : createdBannerId;
            const bannerResponse = targetId
                ? await updateBanner(targetId, formData)
                : await createBanner(formData);

            const bannerId = bannerResponse.data._id;
            if (!targetId) setCreatedBannerId(bannerId);

            const uploadPromises = [];
            if (desktopFile) uploadPromises.push(uploadBannerMedia(bannerId, 'desktop', desktopFile));
            if (tabletFile) uploadPromises.push(uploadBannerMedia(bannerId, 'tablet', tabletFile));
            if (mobileFile) uploadPromises.push(uploadBannerMedia(bannerId, 'mobile', mobileFile));

            if(uploadPromises.length > 0) await Promise.all(uploadPromises);

            setAlert(`Banner "${formData.title}" salvo com sucesso!`, 'success');
            navigate('/site-institucional/banners-home');
        } catch (error) {
            setAlert(error.message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'tags') {
            setFormData(prev => ({ ...prev, tags: value.split(',').map(tag => tag.trim()) }));
        } else if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({ ...prev, [parent]: { ...prev[parent], [child]: value }}));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };
    
    // Remover só limpava o arquivo/preview LOCAL — formData.media (o que
    // realmente vai no PUT de updateBanner) nunca era tocado, então a
    // imagem "removida" continuava intacta no banco: o próximo Salvar
    // reenviava a mesma URL antiga e a remoção não tinha efeito nenhum.
    // Precisa zerar os dois em conjunto.
    const handleRemoveImage = useCallback((deviceType) => {
        const setters = {
            desktop: [setDesktopFile, setDesktopPreview],
            tablet: [setTabletFile, setTabletPreview],
            mobile: [setMobileFile, setMobilePreview]
        };
        const [setFile, setPreview] = setters[deviceType];
        setFile(null);
        setPreview(null);
        setFormData(prev => ({ ...prev, media: { ...prev.media, [deviceType]: '' } }));
    }, []);

    // options.dimensions é o bannerConfig.js inteiro (GET /banners/dimensions)
    // — os tipos ficam aninhados em .types, não na raiz. Sem o .types aqui,
    // isso sempre resultava undefined e a UI silenciosamente só mostrava o
    // nome cru do tipo, sem label/descrição/proporção nenhuma. Declarado
    // aqui (antes de handleUseGeneratedImage) pra poder entrar nas deps do
    // useCallback abaixo sem erro de "usado antes de ser declarado".
    const selectedTypeInfo = options.dimensions.types?.[formData.tipo];

    // Anexa a imagem gerada por IA num dos 3 slots — mesmo estado que o
    // upload manual usa, só o "arquivo escolhido" veio de outro lugar.
    // Gera uma URL de preview NOVA e independente (não reaproveita a do
    // GerarComIA) — aquela tem ciclo de vida próprio lá dentro e seria
    // revogada assim que o admin gerasse outra imagem, quebrando o preview
    // já anexado aqui.
    // ✅ CORREÇÃO: a imagem que a Venice devolve nunca bate exatamente com o
    // preset pedido (proporções muito panorâmicas tipo 46:9 nem existem nos
    // presets dela — ver aiImagePromptUtils.js no server), então sem este
    // resize "cover" pro tamanho exato do slot escolhido, o banner reprovava
    // a validação de largura mínima/proporção (confirmado ao vivo). Mobile
    // usa a proporção própria do tipo quando existe (ex.: 'horizontal'),
    // senão cai na mesma de Desktop/Tablet.
    const handleUseGeneratedImage = useCallback(async (deviceType, file) => {
        const dims = (deviceType === 'mobile' && selectedTypeInfo?.mobile) ? selectedTypeInfo.mobile : selectedTypeInfo;
        if (!dims) return;

        let arquivoFinal = file;
        try {
            arquivoFinal = await resizeImageToCover(file, dims.width, dims.height);
        } catch (err) {
            console.error('Erro ao ajustar imagem gerada ao tamanho do banner:', err);
            setAlert('Erro ao ajustar a imagem gerada ao tamanho do banner. A imagem original foi usada.', 'warning');
        }

        const setters = {
            desktop: [setDesktopFile, setDesktopPreview],
            tablet: [setTabletFile, setTabletPreview],
            mobile: [setMobileFile, setMobilePreview]
        };
        const [setFile, setPreview] = setters[deviceType];
        setFile(arquivoFinal);
        setPreview(URL.createObjectURL(arquivoFinal));
    }, [selectedTypeInfo, setAlert]);

    const handleButtonChange = (index, field, value) => {
        const updatedButtons = [...formData.buttons];
        updatedButtons[index][field] = value;
        setFormData(prev => ({ ...prev, buttons: updatedButtons }));
    };

    const addButton = () => {
        setFormData(prev => ({
            ...prev,
            buttons: [...(prev.buttons || []), { text: '', url: '', target: '_self', variant: 'primary' }]
        }));
    };

    const removeButton = (index) => {
        setFormData(prev => ({
            ...prev,
            buttons: prev.buttons.filter((_, i) => i !== index)
        }));
    };

    const handleItemChange = (index, value) => {
        const updatedItems = [...formData.items];
        updatedItems[index] = value;
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const addItem = () => {
        setFormData(prev => ({ ...prev, items: [...(prev.items || []), ''] }));
    };

    const removeItem = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const totalLoading = hookLoading || isLoading;

    return (
        <div className="page-management-container">
            <form onSubmit={handleSubmit}>
                <PageHeader
                    title={isEditing ? 'Editar Banner' : 'Novo Banner'}
                    icon={<Image className="icon-title" />}
                    navBack={{ to: '/site-institucional/banners-home', text: 'Voltar para Banners' }}
                />

                <div className="content-panel mt-6">
                    <div className="form-grid-container grid-cols-1 md:grid-cols-2">
                        <div className="form-group md:col-span-2"><label>Título*</label><input type="text" name="title" value={formData.title} onChange={handleChange} className="form-input" required /></div>
                        <div className="form-group md:col-span-2"><label>Subtítulo</label><input type="text" name="subtitle" value={formData.subtitle} onChange={handleChange} className="form-input" /></div>
                        <div className="form-group md:col-span-2"><label>Descrição (para banners com texto)</label><textarea name="description" value={formData.description} onChange={handleChange} className="form-input" rows="4"></textarea></div>
                        <div className="form-group">
                            <label>Tipo de Banner (Layout)*</label>
                            <select name="tipo" value={formData.tipo} onChange={handleChange} className="form-input" required>
                                <option value="">Selecione um tipo</option>
                                {options.tipos.map(tipo => <option key={tipo} value={tipo}>{options.dimensions.types?.[tipo]?.label || tipo}</option>)}
                            </select>
                            {selectedTypeInfo && (
                                <p className="form-hint text-blue-600 mt-2">
                                    <Megaphone size={14} className="inline mr-1" />
                                    {selectedTypeInfo.description}
                                    {' '}Tamanho recomendado (Desktop/Tablet): <strong>{selectedTypeInfo.width}×{selectedTypeInfo.height}px</strong> ({selectedTypeInfo.aspectRatio}).
                                    {selectedTypeInfo.mobile && (
                                        <> {' '}Mobile: <strong>{selectedTypeInfo.mobile.width}×{selectedTypeInfo.mobile.height}px</strong> ({selectedTypeInfo.mobile.aspectRatio}).</>
                                    )}
                                </p>
                            )}
                        </div>
                        <div className="form-group"><label>Texto Alternativo da Imagem (SEO)</label><input type="text" name="altText" value={formData.altText} onChange={handleChange} className="form-input" /></div>
                        <div className="form-group"><label>URL do Link (Principal/Fallback)</label><input type="text" name="link.url" value={formData.link.url} onChange={handleChange} className="form-input" /></div>
                        <div className="form-group"><label>Texto do Link (Principal/Fallback)</label><input type="text" name="link.text" value={formData.link.text} onChange={handleChange} className="form-input" /></div>
                    </div>

                    <div className="form-section mt-6 border-t pt-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="form-section-title mb-0">Botões de Ação (CTAs)</h3>
                            <button type="button" onClick={addButton} className="action-button action-button-secondary"><Plus size={16}/> Adicionar Botão</button>
                        </div>
                        <div className="space-y-4">
                            {(formData.buttons || []).map((button, index) => (
                                <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 border rounded bg-gray-50">
                                    <div className="form-group md:col-span-2"><label>Texto do Botão</label><input type="text" value={button.text} onChange={e => handleButtonChange(index, 'text', e.target.value)} className="form-input" /></div>
                                    <div className="form-group md:col-span-2"><label>URL do Botão</label><input type="text" value={button.url} onChange={e => handleButtonChange(index, 'url', e.target.value)} className="form-input" /></div>
                                    <div className="form-group"><label>Abrir em</label><select value={button.target} onChange={e => handleButtonChange(index, 'target', e.target.value)} className="form-input"><option value="_self">Mesma Aba</option><option value="_blank">Nova Aba</option></select></div>
                                    <div className="form-group"><label>Estilo</label><select value={button.variant} onChange={e => handleButtonChange(index, 'variant', e.target.value)} className="form-input"><option value="primary">Primário</option><option value="secondary">Secundário</option><option value="outline-primary">Contorno Primário</option><option value="outline-secondary">Contorno Secundário</option></select></div>
                                    <div className="flex items-end"><button type="button" onClick={() => removeButton(index)} className="action-button action-button-danger w-full"><Trash2 size={16}/> Remover</button></div>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {(formData.tipo === 'left-image-content' || formData.tipo === 'right-image-content') && (
                        <div className="form-section mt-6 border-t pt-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="form-section-title mb-0">Lista de Destaques</h3>
                                <button type="button" onClick={addItem} className="action-button action-button-secondary"><Plus size={16}/> Adicionar Item</button>
                            </div>
                            <p className="form-hint mb-3">Exibidos como uma lista de marcadores ao lado do texto — ex: "Ótima localização", "Profissionais qualificados".</p>
                            <div className="space-y-3">
                                {(formData.items || []).map((item, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <input
                                            type="text"
                                            value={item}
                                            onChange={e => handleItemChange(index, e.target.value)}
                                            className="form-input flex-grow"
                                            placeholder="Ex: Profissionais qualificados"
                                        />
                                        <button type="button" onClick={() => removeItem(index)} className="action-icon-button text-red-600" title="Remover item">
                                            <Trash2 size={16}/>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <GerarComIA
                        tipo={formData.tipo}
                        typeInfo={selectedTypeInfo}
                        onUseImage={handleUseGeneratedImage}
                        generateBannerImage={generateBannerImage}
                    />

                    <div className="space-y-4 border-t pt-6 mt-6">
                        <ImageUploader
                            label="Imagem Desktop*"
                            hint="Imagem principal e obrigatória."
                            file={desktopFile} setFile={setDesktopFile}
                            preview={desktopPreview} setPreview={setDesktopPreview}
                            aspectRatio={selectedTypeInfo?.aspectRatio}
                            onRemove={() => handleRemoveImage('desktop')}
                        />
                        <ImageUploader
                            label="Imagem Tablet (Opcional)"
                            hint="Se vazia, usará a imagem de Desktop."
                            file={tabletFile} setFile={setTabletFile}
                            preview={tabletPreview} setPreview={setTabletPreview}
                            aspectRatio={selectedTypeInfo?.aspectRatio}
                            onRemove={() => handleRemoveImage('tablet')}
                        />
                        <ImageUploader
                            label="Imagem Mobile (Opcional)"
                            hint={
                                selectedTypeInfo?.mobile
                                    ? `Proporção própria pra Mobile: ${selectedTypeInfo.mobile.width}×${selectedTypeInfo.mobile.height}px (${selectedTypeInfo.mobile.aspectRatio}). Se vazia, usará a imagem de Tablet.`
                                    : 'Se vazia, usará a imagem de Tablet.'
                            }
                            file={mobileFile} setFile={setMobileFile}
                            preview={mobilePreview} setPreview={setMobilePreview}
                            aspectRatio={selectedTypeInfo?.mobile?.aspectRatio || selectedTypeInfo?.aspectRatio}
                            onRemove={() => handleRemoveImage('mobile')}
                        />
                    </div>
                </div>

                <FabSaveButton loading={totalLoading} label="Salvar" />
            </form>
        </div>
    );
};

export default BannersHomeFormPage;